/**
 * CustomerSettingsTab - Settings tab content for customer detail modal
 * Extracted from CustomersPage.js
 */
import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerSettingsTab = ({
  customerDetails,
  setCustomerDetails,
  statusChangeLoading,
  setStatusChangeLoading,
  setCustomers,
  setDetailDialogOpen,
  setSelectedCustomer,
  editFormData,
  setEditFormData,
  services,
  pointPackages,
  subscriptionTemplates,
  setPricingDialogOpen,
  pendingCharges,
  setInvoiceDialogOpen
}) => {
  
  // Handler for nutrition access toggle
  const handleNutritionAccessChange = async (checked) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/customers/${customerDetails.id}/profile`,
        { nutrition_access_enabled: checked },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomerDetails({...customerDetails, nutrition_access_enabled: checked});
      toast.success(checked ? 'Ernährungs-App freigeschaltet' : 'Ernährungs-App deaktiviert');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  // Handler for starting / restarting the 7-day nutrition trial.
  // Calls POST /customers/{id}/nutrition-trial/start which sets
  // `nutrition_trial_started_at = now()` on the customer.
  const handleStartNutritionTrial = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/customers/${customerDetails.id}/nutrition-trial/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomerDetails({
        ...customerDetails,
        nutrition_access: data.nutrition_access,
      });
      toast.success('7-Tage-Testphase gestartet');
    } catch (error) {
      toast.error('Fehler beim Starten der Testphase');
    }
  };

  // Handler for deactivating customer
  const handleDeactivateCustomer = async () => {
    if (!window.confirm('Kunde wirklich auf "Inaktiv" setzen? Der Kontakt wird in iCloud in die Inaktiv-Gruppe verschoben.')) return;
    try {
      setStatusChangeLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/customers/${customerDetails.id}/profile`,
        { status: 'inactive' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomers(prev => prev.map(c => 
        c.customer_id === customerDetails.id ? {...c, status: 'inactive'} : c
      ));
      toast.success('Konto deaktiviert - iCloud Kontakt wird in Inaktiv-Gruppe verschoben');
      setDetailDialogOpen(false);
      setSelectedCustomer(null);
    } catch (error) {
      toast.error('Fehler beim Deaktivieren');
    } finally {
      setStatusChangeLoading(false);
    }
  };

  // Iter 339+ Mindbody-Resync (Button oben rechts + im Mindbody-Bereich).
  // Löst die alte Mindbody-Spiegelung aller zukünftigen Termine dieses Kunden
  // auf und pusht sie frisch — hilft bei nicht-löschbaren Serien-Termine.
  const [mindbodyResyncing, setMindbodyResyncing] = useState(false);
  const handleMindbodyResync = async () => {
    if (!customerDetails?.id) return;
    if (!window.confirm(
      `Mindbody-Termine für "${customerDetails.first_name} ${customerDetails.last_name}" neu synchronisieren?\n\n`
      + 'Alle bestehenden Mindbody-Spiegelungen werden gelöscht und mit dem '
      + 'aktuellen StrongFit-Stand neu gepusht. Nur zukünftige Termine sind betroffen.'
    )) return;
    setMindbodyResyncing(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      // 08/2026 (524-Timeout-Fix): Resync läuft jetzt als Background-Job.
      // Der POST startet den Job und wir pollen den Status alle 3s.
      const res = await axios.post(
        `${API}/admin/mindbody/customers/${customerDetails.id}/resync`,
        {},
        { headers }
      );
      const start = res.data || {};
      if (!start.started) {
        if (start.already_running) {
          toast.info('Für diesen Kunden läuft bereits ein Mindbody-Resync.');
        } else {
          toast.info(start.message || 'Keine zukünftigen Termine zum Resyncen.');
          setMindbodyResyncing(false);
          return;
        }
      } else {
        toast.info(`Mindbody-Resync gestartet: ${start.total} Termine werden im Hintergrund neu gespiegelt…`);
      }

      // Polling bis der Job fertig ist (max. ~20 Min)
      const poll = async () => {
        const stRes = await axios.get(
          `${API}/admin/mindbody/customers/${customerDetails.id}/resync-status`,
          { headers }
        );
        return stRes.data || {};
      };
      let job = {};
      for (let i = 0; i < 400; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          job = await poll();
        } catch {
          continue;
        }
        if (job.status && job.status !== 'running') break;
      }
      if (job.status === 'done') {
        if ((job.error_count || 0) === 0) {
          toast.success(`Mindbody-Resync erfolgreich: ${job.repushed} Termine neu gespiegelt (${job.deleted || 0} alte Einträge ersetzt).`);
        } else {
          toast.warning(
            `Resync teilweise: ${job.repushed} ok, ${job.error_count} Fehler. Details siehe Konsole.`
          );
          console.warn('Mindbody-Resync Fehler:', job.errors);
        }
      } else if (job.status === 'failed') {
        toast.error(`Mindbody-Resync fehlgeschlagen: ${job.error || 'Unbekannter Fehler'}`);
      } else if (job.status === 'running') {
        toast.info(`Resync läuft noch im Hintergrund (${job.processed || 0}/${job.total || '?'} Termine) – du kannst das Fenster schliessen.`);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Fehler beim Mindbody-Resync';
      toast.error(msg);
    } finally {
      setMindbodyResyncing(false);
    }
  };

  // Handler for activating customer
  const handleActivateCustomer = async () => {
    if (!window.confirm('Kunde reaktivieren? Es wird eine Willkommens-E-Mail und WhatsApp-Nachricht gesendet.')) return;
    try {
      setStatusChangeLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/customers/${customerDetails.id}/profile`,
        { status: 'active' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomers(prev => prev.map(c => 
        c.customer_id === customerDetails.id ? {...c, status: 'active'} : c
      ));
      toast.success('Konto aktiviert - Willkommens-E-Mail & WhatsApp gesendet, iCloud-Kontakt in Aktiv-Gruppe verschoben');
      setDetailDialogOpen(false);
      setSelectedCustomer(null);
    } catch (error) {
      toast.error('Fehler beim Aktivieren');
    } finally {
      setStatusChangeLoading(false);
    }
  };

  // Handler for suit rental toggle
  const handleSuitRentalChange = async (checked) => {
    if (checked && !customerDetails.suit_rental_amount) {
      const amount = prompt('Monatlicher Mietbetrag in CHF:', '40');
      if (!amount || isNaN(parseFloat(amount))) {
        toast.error('Bitte geben Sie einen gültigen Betrag ein');
        return;
      }
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `${API}/customers/${customerDetails.id}/profile`,
          { suit_rental_active: true, suit_rental_amount: parseFloat(amount) },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCustomerDetails({
          ...customerDetails, 
          suit_rental_active: true,
          suit_rental_amount: parseFloat(amount)
        });
        toast.success(`Anzug-Miete aktiviert: CHF ${parseFloat(amount).toFixed(2)}/Monat`);
      } catch (error) {
        toast.error('Fehler beim Aktivieren der Anzug-Miete');
      }
    } else {
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `${API}/customers/${customerDetails.id}/profile`,
          { suit_rental_active: checked },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCustomerDetails({...customerDetails, suit_rental_active: checked});
        toast.success(checked ? 'Anzug-Miete aktiviert' : 'Anzug-Miete deaktiviert');
      } catch (error) {
        toast.error('Fehler beim Aktualisieren');
      }
    }
  };

  // Handler for saving suit rental amount
  const handleSaveSuitRentalAmount = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/customers/${customerDetails.id}/profile`,
        { suit_rental_amount: customerDetails.suit_rental_amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Betrag aktualisiert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  // Handler: genereller Rabatt speichern (löst Neuberechnung Termine + offene Rechnungen aus)
  const [savingDiscount, setSavingDiscount] = React.useState(false);
  const handleSaveDiscount = async () => {
    setSavingDiscount(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API}/customers/${customerDetails.id}/profile`,
        { discount_percent: parseFloat(editFormData.discount_percent) || 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.reprice_job_id) {
        toast.success('Rabatt gespeichert! Termine & offene Rechnungen werden im Hintergrund neu berechnet.');
      } else {
        toast.success('Rabatt gespeichert!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern des Rabatts');
    } finally {
      setSavingDiscount(false);
    }
  };

  // 06/2026: Separater Rabatt NUR für Punkte-Kauf & Service-Abo. Höherer von
  // (Mengenrabatt, Kundenrabatt) gewinnt — keine Reprice nötig (nur Neukäufe).
  const [savingPointsDiscount, setSavingPointsDiscount] = React.useState(false);
  const handleSavePointsAboDiscount = async () => {
    setSavingPointsDiscount(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/customers/${customerDetails.id}/profile`,
        { points_abo_discount_percent: parseFloat(editFormData.points_abo_discount_percent) || 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Punkte/Abo-Rabatt gespeichert!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingPointsDiscount(false);
    }
  };


  const serviceMap = useMemo(() => {
    const map = new Map();
    (services || []).forEach(service => {
      map.set(service.id, service);
    });
    return map;
  }, [services]);

  const packageMap = useMemo(() => {
    const map = new Map();
    (pointPackages || []).forEach(pkg => {
      map.set(pkg.id, pkg);
    });
    return map;
  }, [pointPackages]);

  const subscriptionMap = useMemo(() => {
    const map = new Map();
    (subscriptionTemplates || []).forEach(template => {
      map.set(template.id, template);
    });
    return map;
  }, [subscriptionTemplates]);

  const individualPriceEntries = useMemo(() => {
    return Object.entries(editFormData.individual_prices || {})
      .map(([serviceId, price]) => {
        const service = serviceMap.get(serviceId);
        if (!service || price === undefined || price === null || price === '') return null;
        const priceNum = parseFloat(price);
        if (isNaN(priceNum)) return null;
        return { serviceId, service, priceNum };
      })
      .filter(Boolean);
  }, [editFormData.individual_prices, serviceMap]);

  const individualPackageEntries = useMemo(() => {
    return Object.entries(editFormData.individual_package_prices || {})
      .map(([packageId, price]) => {
        const pkg = packageMap.get(packageId);
        if (!pkg || price === undefined || price === null || price === '') return null;
        const priceNum = parseFloat(price);
        if (isNaN(priceNum)) return null;
        return { packageId, pkg, priceNum };
      })
      .filter(Boolean);
  }, [editFormData.individual_package_prices, packageMap]);

  const individualSubscriptionEntries = useMemo(() => {
    return Object.entries(editFormData.individual_subscription_prices || {})
      .map(([templateId, price]) => {
        const template = subscriptionMap.get(templateId);
        if (!template || price === undefined || price === null || price === '') return null;
        const priceNum = parseFloat(price);
        if (isNaN(priceNum)) return null;
        return { templateId, template, priceNum };
      })
      .filter(Boolean);
  }, [editFormData.individual_subscription_prices, subscriptionMap]);

  return (
    <div className="space-y-6" data-testid="customer-settings-tab">
      {/* Feature Access Controls */}
      <div className="p-4 bg-purple-50 rounded-lg border border-purple-200" data-testid="settings-access-section">
        <h4 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
          🔐 Funktions-Freischaltungen
        </h4>
        
        {/* Nutrition Access Toggle */}
        <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🍎</span>
            <div>
              <p className="font-medium">Ernährungs-App</p>
              <p className="text-xs text-gray-500">
                {customerDetails.nutrition_access_enabled 
                  ? 'Manuell freigeschaltet' 
                  : `Automatisch nach 10 Trainings & 1 Training/Monat`
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!customerDetails.nutrition_access_enabled && (
              <span className="text-xs text-gray-500">
                {customerDetails.nutrition_access?.total_completed || 0}/10 Trainings
              </span>
            )}
            <Switch
              checked={customerDetails.nutrition_access_enabled || false}
              onCheckedChange={handleNutritionAccessChange}
              data-testid="settings-nutrition-toggle"
            />
          </div>
        </div>

        {/* Nutrition Trial Status — only shown when admin-toggle is OFF,
            because admin_enabled takes priority over trial in backend logic. */}
        {!customerDetails.nutrition_access_enabled && (
          <div
            className="mt-2 p-3 bg-white rounded-lg border"
            data-testid="settings-nutrition-trial-section"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1">
                <span className="text-2xl">⏱️</span>
                <div className="flex-1">
                  <p className="font-medium text-sm">7-Tage-Testphase</p>
                  {customerDetails.nutrition_access?.reason === 'trial' ? (
                    <p className="text-xs text-green-700 font-medium">
                      Läuft — noch {customerDetails.nutrition_access.trial_days_remaining} {customerDetails.nutrition_access.trial_days_remaining === 1 ? 'Tag' : 'Tage'}
                    </p>
                  ) : customerDetails.nutrition_access?.trial_expired ? (
                    <p className="text-xs text-orange-600">
                      Abgelaufen — Kunde sieht Locked-State mit {customerDetails.nutrition_access.total_completed || 0}/10 Training-Fortschritt
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500">
                      Noch nicht gestartet oder abgelaufen
                    </p>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleStartNutritionTrial}
                data-testid="settings-nutrition-trial-restart-btn"
              >
                {customerDetails.nutrition_access?.reason === 'trial' ? 'Neu starten' : 'Starten'}
              </Button>
            </div>
          </div>
        )}
        
        {/* Customer Status */}
        <div className="flex items-center justify-between p-3 bg-white rounded-lg border mt-2">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{customerDetails.status === 'active' ? '✅' : '⏸️'}</span>
            <div>
              <p className="font-medium">Konto-Status</p>
              <p className="text-xs text-gray-500">
                {customerDetails.status === 'active' 
                  ? 'Aktiv - Voller Zugang zum Portal' 
                  : 'Inaktiv - Wird nach 30 Tagen ohne Termine gesetzt'
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              customerDetails.status === 'active' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {customerDetails.status === 'active' ? 'Aktiv' : 'Inaktiv'}
            </span>
            {customerDetails.status === 'active' && (
              <Button
                size="sm"
                variant="outline"
                className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                disabled={statusChangeLoading}
                onClick={handleDeactivateCustomer}
                data-testid="settings-deactivate-customer"
              >
                {statusChangeLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-orange-600 border-t-transparent rounded-full"></span>
                    Wird deaktiviert...
                  </span>
                ) : 'Deaktivieren'}
              </Button>
            )}
            {customerDetails.status !== 'active' && (
              <Button
                size="sm"
                variant="outline"
                className="bg-green-50 text-green-700 hover:bg-green-100 border-green-300"
                disabled={statusChangeLoading}
                onClick={handleActivateCustomer}
                data-testid="settings-activate-customer"
              >
                {statusChangeLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full"></span>
                    Wird aktiviert...
                  </span>
                ) : '✅ Aktivieren'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* EMS Anzug-Miete Section */}
      <div className="p-4 bg-orange-50 rounded-lg border border-orange-200" data-testid="settings-suit-rental-section">
        <h4 className="font-semibold text-orange-900 mb-3 flex items-center gap-2">
          👔 EMS Anzug-Miete
        </h4>
        
        <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
          <div className="flex items-center gap-3">
            <span className="text-2xl">💼</span>
            <div>
              <p className="font-medium">Monatliche Anzug-Miete</p>
              <p className="text-xs text-gray-500">
                {customerDetails.suit_rental_active 
                  ? `Aktiv - CHF ${customerDetails.suit_rental_amount?.toFixed(2) || '0.00'}/Monat` 
                  : 'Nicht aktiv'
                }
              </p>
              {customerDetails.suit_rental_active && customerDetails.suit_rental_next_billing && (
                <p className="text-xs text-orange-600">
                  📅 Nächste Abbuchung: {new Date(customerDetails.suit_rental_next_billing).toLocaleDateString('de-CH')}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={customerDetails.suit_rental_active || false}
              onCheckedChange={handleSuitRentalChange}
              data-testid="settings-suit-rental-toggle"
            />
          </div>
        </div>
        
        {/* Amount Edit */}
        {customerDetails.suit_rental_active && (
          <div className="mt-3 p-3 bg-white rounded-lg border">
            <Label className="text-sm text-gray-600">Monatlicher Betrag (CHF)</Label>
            <div className="flex gap-2 mt-1">
              <Input
                type="number"
                step="0.01"
                min="0"
                value={customerDetails.suit_rental_amount || ''}
                onChange={(e) => setCustomerDetails({
                  ...customerDetails, 
                  suit_rental_amount: parseFloat(e.target.value) || 0
                })}
                className="w-32"
                data-testid="settings-suit-rental-amount-input"
              />
              <Button size="sm" onClick={handleSaveSuitRentalAmount} data-testid="settings-suit-rental-save">
                Speichern
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Individual Pricing Section */}
      <div className="p-4 bg-gray-50 rounded-lg border" data-testid="settings-pricing-section">
        <div className="flex justify-between items-center mb-3">
          <div>
            <h4 className="font-semibold">Individuelle Preise</h4>
            <p className="text-sm text-gray-600">Spezielle Preise für diesen Kunden festlegen</p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPricingDialogOpen(true)}
            data-testid="settings-pricing-manage"
          >
            Preise verwalten
          </Button>
        </div>
        
        {/* Show current individual prices */}
        {(individualPriceEntries.length > 0 || individualPackageEntries.length > 0 || individualSubscriptionEntries.length > 0) && (
          <div className="space-y-3" data-testid="settings-pricing-list">
            {individualPriceEntries.length > 0 && (
              <div className="space-y-2" data-testid="settings-pricing-services">
                <p className="text-xs font-semibold text-gray-500 uppercase">Services</p>
                {individualPriceEntries.map(({ serviceId, service, priceNum }) => (
                  <div key={serviceId} className="flex justify-between text-sm p-2 bg-white rounded" data-testid={`settings-pricing-item-${serviceId}`}>
                    <span>{service.name}</span>
                    <span className="font-semibold">
                      CHF {priceNum.toFixed(2)} 
                      <span className="text-gray-400">(Standard: CHF {service.price?.toFixed(2)})</span>
                    </span>
                  </div>
                ))}
              </div>
            )}

            {individualPackageEntries.length > 0 && (
              <div className="space-y-2" data-testid="settings-pricing-packages">
                <p className="text-xs font-semibold text-gray-500 uppercase">Punkte-Pakete</p>
                {individualPackageEntries.map(({ packageId, pkg, priceNum }) => (
                  <div key={packageId} className="flex justify-between text-sm p-2 bg-white rounded" data-testid={`settings-pricing-package-${packageId}`}>
                    <span>{pkg.name}</span>
                    <span className="font-semibold">
                      CHF {priceNum.toFixed(2)} 
                      <span className="text-gray-400">(Standard: CHF {pkg.price?.toFixed(2)})</span>
                    </span>
                  </div>
                ))}
              </div>
            )}

            {individualSubscriptionEntries.length > 0 && (
              <div className="space-y-2" data-testid="settings-pricing-subscriptions">
                <p className="text-xs font-semibold text-gray-500 uppercase">Abos</p>
                {individualSubscriptionEntries.map(({ templateId, template, priceNum }) => (
                  <div key={templateId} className="flex justify-between text-sm p-2 bg-white rounded" data-testid={`settings-pricing-subscription-${templateId}`}>
                    <span>{template.name}</span>
                    <span className="font-semibold">
                      CHF {priceNum.toFixed(2)} 
                      <span className="text-gray-400">(Standard: CHF {template.price?.toFixed(2)})</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Rabatte (nur Anzeige) — Bearbeitung ausschließlich über "Preise verwalten" */}
        <div className="mt-4 pt-4 border-t space-y-2" data-testid="settings-discount-display">
          <p className="text-xs font-semibold text-gray-500 uppercase">Rabatte</p>
          {(() => {
            const svc = Number(customerDetails?.discount_percent) || 0;
            const pts = Number(customerDetails?.points_discount_percent ?? customerDetails?.points_abo_discount_percent) || 0;
            const abo = Number(customerDetails?.service_abo_discount_percent ?? customerDetails?.points_abo_discount_percent) || 0;
            if (svc === 0 && pts === 0 && abo === 0) {
              return <p className="text-sm text-gray-400">Keine Rabatte hinterlegt.</p>;
            }
            const rows = [
              { label: 'Dienstleistungen', val: svc, testid: 'discount-display-services' },
              { label: 'Punkte-Käufe', val: pts, testid: 'discount-display-points' },
              { label: 'Service-Abos', val: abo, testid: 'discount-display-abo' },
            ];
            return rows.map((r) => (
              <div key={r.testid} className="flex justify-between text-sm p-2 bg-white rounded" data-testid={r.testid}>
                <span>{r.label}</span>
                <span className={`font-semibold ${r.val > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                  {r.val > 0 ? `${r.val}% Rabatt` : '–'}
                </span>
              </div>
            ));
          })()}
          <p className="text-xs text-gray-500">
            Rabatte werden unter „Preise verwalten" bearbeitet.
          </p>
        </div>
      </div>
      
      {/* Pending Charges Display for Ende Monat Customers */}
      {editFormData.payment_timing === 'ende_monat' && pendingCharges && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-testid="settings-pending-charges">
          <h4 className="font-semibold text-yellow-800 mb-2">Offene Trainings (Ende Monat)</h4>
          <div className="text-sm text-yellow-700">
            <p><strong>{pendingCharges.total_count}</strong> offene Trainings im Wert von <strong>CHF {pendingCharges.total_amount?.toFixed(2)}</strong></p>
          </div>
          {pendingCharges.total_count > 0 && (
            <Button 
              size="sm" 
              className="mt-3"
              onClick={() => setInvoiceDialogOpen(true)}
              data-testid="settings-create-invoice"
            >
              Rechnung jetzt erstellen
            </Button>
          )}
        </div>
      )}

      {/* Iter 339+ Mindbody-Resync — eigene Sektion, klar abgetrennt */}
      <div className="border-t pt-4 space-y-2" data-testid="mindbody-resync-section">
        <Label className="text-gray-600 mb-1 block">🔄 Mindbody-Synchronisation</Label>
        <p className="text-xs text-gray-500">
          Wenn in Mindbody alte Serien-Termine hängen die sich nicht löschen lassen:
          dieser Resync stösst alle zukünftigen Termine dieses Kunden neu an
          (alte Spiegelung wird gelöscht, aktuelle StrongFit-Termine neu nach
          Mindbody gepusht). Vergangene Termine bleiben unberührt.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={handleMindbodyResync}
          disabled={mindbodyResyncing}
          data-testid="mindbody-resync-button"
          className="mt-1"
        >
          {mindbodyResyncing ? '🔄 Synchronisiere…' : '🔄 Mit Mindbody neu synchronisieren'}
        </Button>
      </div>

      {/* Signature Display */}
      {customerDetails.signature_data && (
        <div className="border-t pt-4">
          <Label className="text-gray-600 mb-2 block">Haftungsausschluss Unterschrift</Label>
          <img 
            src={customerDetails.signature_data} 
            alt="Signature" 
            className="border rounded-lg max-w-md"
          />
          <p className="text-sm text-gray-600 mt-2">
            Unterschrieben am: {new Date(customerDetails.signed_at).toLocaleString('de-DE')}
          </p>
        </div>
      )}
    </div>
  );
};

export default React.memo(CustomerSettingsTab);
